import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";
import { Logo, LogoMark } from "@/components/brand/Logo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      nav(u.role === "client" ? "/portale" : "/admin");
    } catch (err) {
      setError(formatErr(err));
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden flex-col justify-between overflow-hidden bg-black p-12 text-white lg:flex">
        <Logo size={34} inverted testId="login-brand-logo" />
        <div className="relative z-10 fade-up">
          <h1 className="font-display text-6xl leading-[1.02]">Biglietti da visita.<br /><span className="text-zinc-500">Reinventati.</span></h1>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-zinc-400">Crea, pubblica e gestisci le digital card dei tuoi clienti. Un QR permanente, una landing curata, un contatto salvabile in un tocco.</p>
        </div>
        <div className="flex gap-10 text-xs text-zinc-500"><span>vCard 3.0</span><span>QR dinamici</span><span>Landing mobile-first</span></div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-[radial-gradient(ellipse_at_bottom,rgba(200,90,50,0.28),transparent_65%)]" />
        <div className="pointer-events-none absolute inset-x-12 bottom-[22%] h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
      </div>
      <div className="flex items-center justify-center bg-white px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <LogoMark size={40} className="mb-6 lg:hidden" />
          <div className="eyebrow mb-3">Area riservata</div>
          <h2 className="font-display text-4xl">Accedi.</h2>
          <p className="mt-2 text-sm text-zinc-500">Area riservata a SALVILO e ai suoi clienti.</p>
          <div className="mt-8 space-y-4">
            <div><Label className="text-xs">Email</Label><Input data-testid="login-email-input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12 rounded-xl" /></div>
            <div><Label className="text-xs">Password</Label><Input data-testid="login-password-input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12 rounded-xl" /></div>
            {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
            <div className="text-right"><Link to="/password-dimenticata" className="text-xs text-zinc-500 hover:text-black" data-testid="login-forgot-link">Password dimenticata?</Link></div>
            <Button type="submit" disabled={busy} className="h-12 w-full" data-testid="login-submit-button">{busy ? "Accesso…" : <>Entra <ArrowRight className="ml-2 h-4 w-4" /></>}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
